import { useState, type ReactNode } from 'react';
import { Link } from 'react-router';

import { CeMark } from './ce-mark';
import {
    ConsultIfuSymbol,
    DateOfManufactureSymbol,
    MdSymbol,
    ManufacturerSymbol,
    UdiSymbol,
} from './symbols';
import { UdiDataMatrix } from './udi-datamatrix';

import { Button } from '@/components/ui/button';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Separator } from '@/components/ui/separator';
import { useAuth0 } from '@/context';
import { useMyAcceptances } from '@/hooks/use-legal';
import {
    CE_NOTIFIED_BODY_NUMBER,
    MANUFACTURER,
    PRODUCT_DESCRIPTION,
    PRODUCT_NAME,
    REGULATORY,
    UDI_DI,
} from '@/lib/regulatory';

interface AboutDialogProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
}

function SectionTitle({ children }: { children: ReactNode }) {
    return (
        <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{children}</h3>
    );
}

/**
 * Regulatory labelling dialog (ESCLOUD-76 / ESCLOUD-180): product identity,
 * manufacturer, regulatory marks and UDI, plus eIFU / EU DoC access.
 */
export function AboutDialog({ open, onOpenChange }: AboutDialogProps) {
    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-lg">
                <DialogHeader>
                    <DialogTitle>About {PRODUCT_NAME}</DialogTitle>
                    <DialogDescription>Regulatory labelling information</DialogDescription>
                </DialogHeader>

                {/* Product identity */}
                <div className="flex items-center gap-4">
                    <MdSymbol className="h-14 w-14 shrink-0" />
                    <div>
                        <p className="text-lg font-semibold">{PRODUCT_NAME}</p>
                        <p className="text-sm text-muted-foreground">{PRODUCT_DESCRIPTION}</p>
                        <p className="text-sm">
                            Software version: <span className="font-mono">{REGULATORY.softwareVersion}</span>
                        </p>
                    </div>
                </div>

                <Separator />

                {/* Manufacturer + date of manufacture */}
                <div className="space-y-3">
                    <div className="flex items-center gap-3">
                        <ManufacturerSymbol className="h-16 w-16 shrink-0" />
                        <div>
                            <SectionTitle>Manufacturer</SectionTitle>
                            <p className="text-sm">{MANUFACTURER.name}</p>
                            <p className="text-sm">{MANUFACTURER.address}</p>
                            <p className="text-sm text-muted-foreground">
                                {MANUFACTURER.email} — {MANUFACTURER.website}
                            </p>
                        </div>
                    </div>
                    <div className="flex items-center gap-3">
                        <DateOfManufactureSymbol className="h-16 w-16 shrink-0" />
                        <div>
                            <SectionTitle>Date of manufacture</SectionTitle>
                            <p className="font-mono text-sm">{REGULATORY.releaseDate}</p>
                        </div>
                    </div>
                </div>

                <Separator />

                {/* Regulatory marks */}
                <div className="space-y-2">
                    <SectionTitle>Regulatory marks</SectionTitle>
                    <div className="flex items-center gap-4">
                        <div className="flex w-16 flex-col items-center gap-1">
                            <CeMark className="h-10 w-auto" />
                            <span className="text-lg">{CE_NOTIFIED_BODY_NUMBER}</span>
                        </div>
                        <p className="text-sm">
                            FDA 510(k):{' '}
                            <span className="font-mono">{REGULATORY.fda510kNumber ?? 'Pending'}</span>
                        </p>
                    </div>
                </div>

                <Separator />

                {/* UDI */}
                <div className="space-y-3">
                    <div className="flex items-center gap-3">
                        <UdiSymbol className="h-16 w-16 shrink-0" />
                        <div>
                            <SectionTitle>Unique device identifier</SectionTitle>
                            <p className="text-sm">
                                UDI-DI: <span className="font-mono">{UDI_DI}</span>
                            </p>
                            <p className="text-sm">
                                UDI-PI: <span className="font-mono">{REGULATORY.udiPi}</span>
                            </p>
                        </div>
                    </div>
                    <div className="flex items-center gap-4">
                        <UdiDataMatrix className="h-16 w-16 shrink-0" />
                        <div className="text-sm">
                            <p className="text-muted-foreground">GS1 DataMatrix encoding:</p>
                            <p className="font-mono">{REGULATORY.gs1.ai01}</p>
                            <p className="font-mono">{REGULATORY.gs1.ai11}</p>
                            <p className="font-mono">{REGULATORY.gs1.ai8012}</p>
                        </div>
                    </div>
                </div>

                <Separator />

                {/* eIFU + EU DoC (ESCLOUD-77) */}
                <div className="space-y-2">
                    <div className="flex items-center gap-3">
                        <ConsultIfuSymbol className="h-16 w-16 shrink-0" />
                        <div className="flex flex-col items-start gap-2">
                            <Button variant="link" className="h-auto p-0" asChild>
                                <Link to="/eifu" onClick={() => onOpenChange(false)}>
                                    View electronic Instructions for Use (eIFU)
                                </Link>
                            </Button>
                            <Button variant="link" className="h-auto p-0" asChild>
                                <Link to="/declaration-of-conformity" onClick={() => onOpenChange(false)}>
                                    View EU Declaration of Conformity (DoC)
                                </Link>
                            </Button>
                        </div>
                    </div>
                </div>

                <PastEulaVersions />
            </DialogContent>
        </Dialog>
    );
}

/**
 * "Past EULA versions" (ESCLOUD-79): every EULA version the signed-in user has
 * accepted, from the acceptance history endpoint. Hidden on unauthenticated
 * screens — there is no acceptance history without a user.
 */
function PastEulaVersions() {
    const { isAuthenticated } = useAuth0();
    if (!isAuthenticated) {
        return null;
    }
    return <PastEulaVersionsContent />;
}

function PastEulaVersionsContent() {
    const [open, setOpen] = useState(false);
    const { data: acceptances, isPending, isError } = useMyAcceptances({ enabled: open });
    const eulaAcceptances = acceptances?.filter((acceptance) => acceptance.documentType === 'eula');

    return (
        <>
            <Separator />
            <Collapsible open={open} onOpenChange={setOpen} className="space-y-2">
                <CollapsibleTrigger asChild>
                    <Button variant="link" className="h-auto p-0">
                        Past EULA versions
                    </Button>
                </CollapsibleTrigger>
                <CollapsibleContent>
                    {isPending && <p className="text-sm text-muted-foreground">Loading…</p>}
                    {isError && (
                        <p className="text-sm text-destructive">
                            The acceptance history could not be loaded. Please try again later.
                        </p>
                    )}
                    {eulaAcceptances && eulaAcceptances.length === 0 && (
                        <p className="text-sm text-muted-foreground">No accepted EULA version yet.</p>
                    )}
                    {eulaAcceptances && eulaAcceptances.length > 0 && (
                        <ul className="space-y-1 text-sm">
                            {eulaAcceptances.map((acceptance) => (
                                <li key={acceptance.id} className="flex justify-between gap-2">
                                    <span>
                                        Version{' '}
                                        <span className="font-mono">{acceptance.documentVersion}</span>
                                    </span>
                                    <span className="text-muted-foreground">
                                        accepted {new Date(acceptance.acceptedAt).toLocaleDateString()}
                                    </span>
                                </li>
                            ))}
                        </ul>
                    )}
                </CollapsibleContent>
            </Collapsible>
        </>
    );
}
